import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Crown, Coins, Loader2, ExternalLink, Check, Infinity as InfinityIcon } from "lucide-react";
import { billingApi } from "@/api/billing";
import S20DualCheckoutPanel from "@/components/billing/S20DualCheckoutPanel";

// STAGE 6 · PHASE 5 — pricing UI. Select a subscription, coin bundle or the
// Lifetime Pass → opens the unified dual-gateway (Stripe / PayPal) checkout panel.

const fmt = (amt, cur) => (amt == null ? "—" : `${(amt / 100).toLocaleString(undefined, { style: "currency", currency: (cur || "usd").toUpperCase() })}`);

export default function PlansPanel({ onBack }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null); // active product → dual checkout

  useEffect(() => {
    billingApi.plans()
      .then((d) => setPlans(d.plans || []))
      .catch(() => toast.error("Could not load plans"))
      .finally(() => setLoading(false));
  }, []);

  const subs = plans.filter((p) => p.kind === "subscription");
  const bundles = plans.filter((p) => p.kind === "coins");
  const lifetime = plans.filter((p) => p.kind === "lifetime");

  return (
    <div className="absolute inset-0 z-40 flex flex-col bg-slate-950" data-testid="plans-panel">
      <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3">
        <button onClick={onBack} data-testid="plans-back" className="grid h-9 w-9 place-items-center rounded-xl border-2 border-white/15 bg-white/5 active:scale-95">
          <ArrowLeft size={16} className="text-white" />
        </button>
        <div className="leading-tight">
          <p className="font-mono text-[9px] uppercase tracking-[0.24em] text-gold">Billing</p>
          <h2 className="font-serif text-xl text-white">Plans & Credits</h2>
        </div>
      </div>

      {loading ? (
        <div className="flex flex-1 items-center justify-center"><Loader2 className="animate-spin text-white/50" /></div>
      ) : (
        <div className="flex-1 space-y-5 overflow-y-auto no-scrollbar p-4">
          <div>
            <p className="mb-2 flex items-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-[0.22em] text-gold"><Crown size={13} /> Subscription</p>
            <div className="space-y-2.5">
              {subs.map((p) => (
                <div key={p.lookup_key} data-testid={`plan-${p.lookup_key}`} className="flex items-center gap-3 rounded-2xl border-2 border-gold/30 bg-gold/[0.06] p-3.5">
                  <div className="flex-1 leading-tight">
                    <p className="font-sans text-[15px] font-bold text-white">{p.label}</p>
                    <p className="font-mono text-[9px] uppercase tracking-wide text-white/45">Unlocks Tier-2 access · Fair-Play cap</p>
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-lg text-gold">{fmt(p.amount, p.currency)}</p>
                    <p className="font-mono text-[8px] uppercase text-white/40">/{p.interval}</p>
                  </div>
                  <button onClick={() => setSelected(p)} data-testid={`buy-${p.lookup_key}`}
                    className="flex items-center gap-1 rounded-full border-2 border-gold bg-gold px-3 py-2 font-mono text-[10px] font-black uppercase tracking-wide text-slate-950 active:scale-95">
                    <ExternalLink size={13} /> Subscribe
                  </button>
                </div>
              ))}
            </div>
          </div>

          {lifetime.length > 0 && (
            <div>
              <p className="mb-2 flex items-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-[0.22em] text-[#FFCC00]"><InfinityIcon size={13} /> Lifetime Access</p>
              <div className="space-y-2.5">
                {lifetime.map((p) => (
                  <button key={p.lookup_key} onClick={() => setSelected(p)} data-testid={`buy-${p.lookup_key}`}
                    className="w-full flex items-center gap-3 rounded-2xl border-2 border-[#FFCC00]/40 bg-gradient-to-r from-purple-950/40 to-black p-3.5 text-left active:scale-[0.99]">
                    <span className="grid h-11 w-11 place-items-center rounded-xl border-2 border-[#FFCC00]/50 bg-black shrink-0"><InfinityIcon size={20} className="text-[#FFCC00]" /></span>
                    <div className="flex-1 leading-tight">
                      <p className="font-sans text-[15px] font-bold text-white">{p.label}</p>
                      <p className="font-mono text-[9px] uppercase tracking-wide text-white/45">Permanent ad-free access · one payment</p>
                    </div>
                    <p className="font-serif text-lg text-[#FFCC00] whitespace-nowrap">{fmt(p.amount, p.currency)}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div>
            <p className="mb-2 flex items-center gap-1.5 font-mono text-[10px] font-black uppercase tracking-[0.22em] text-sky-300"><Coins size={13} /> Casual Credit Bundles</p>
            <div className="grid grid-cols-2 gap-2.5">
              {bundles.map((p) => (
                <button key={p.lookup_key} onClick={() => setSelected(p)} data-testid={`buy-${p.lookup_key}`}
                  className="flex flex-col items-start gap-2 rounded-2xl border-2 border-sky-400/30 bg-sky-500/[0.06] p-3.5 text-left active:translate-x-[2px] active:translate-y-[2px]">
                  <Coins size={20} className="text-sky-300" />
                  <p className="font-sans text-[15px] font-bold text-white">{p.coins} Credits</p>
                  <p className="font-serif text-lg text-sky-200">{fmt(p.amount, p.currency)}</p>
                  <span className="flex items-center gap-1 font-mono text-[9px] font-black uppercase tracking-wide text-sky-300">
                    <Check size={12} /> Buy
                  </span>
                </button>
              ))}
            </div>
          </div>

          <p className="font-mono text-[8px] leading-relaxed text-white/30">Sandbox checkout — use test card 4242 4242 4242 4242, any future expiry & CVC.</p>
        </div>
      )}

      {selected && <S20DualCheckoutPanel product={selected} onBack={() => setSelected(null)} />}
    </div>
  );
}
